<script setup>
import { ref, computed, defineProps, defineEmits, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import KanbanHeader from './KanbanHeader.vue';
import KanbanAPI from '../../../../api/kanban';

const props = defineProps({
  currentView: {
    type: String,
    default: 'dashboard',
  },
});

const emit = defineEmits(['switchView']);

const store = useStore();

// Funil selecionado, igual base usado em KanbanTab/ListTab
const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);

// Estatísticas agregadas vindas da API de funis (index)
const funnelStats = computed(() => selectedFunnel.value?.stats || null);

// Itens do Kanban carregados da API para o funil atual (como em ListTab)
const kanbanItems = ref([]);
const isLoadingItems = ref(false);

const fetchKanbanItems = async () => {
  if (!selectedFunnel.value?.id) {
    kanbanItems.value = [];
    return;
  }

  try {
    isLoadingItems.value = true;
    const response = await KanbanAPI.getItems(selectedFunnel.value.id);
    const items = response.data.items || response.data || [];
    kanbanItems.value = items;
  } catch (error) {
    console.error('[KanbanDashboard] Erro ao buscar itens do funil:', error);
    kanbanItems.value = [];
  } finally {
    isLoadingItems.value = false;
  }
};

onMounted(() => {
  if (selectedFunnel.value) {
    fetchKanbanItems();
  }
});

watch(selectedFunnel, (newFunnel, oldFunnel) => {
  if (newFunnel && newFunnel.id !== oldFunnel?.id) {
    fetchKanbanItems();
  }
});

// Itens apenas do funil atual (defensivo, embora a API já filtre por funil)
const funnelItems = computed(() => {
  if (!selectedFunnel.value) return [];
  return (kanbanItems.value || []).filter(
    item => String(item.funnel_id) === String(selectedFunnel.value.id)
  );
});

// Colunas (estágios) derivadas do funil, como no KanbanTab
const columns = computed(() => {
  const funnel = selectedFunnel.value;
  if (!funnel?.stages) return [];

  const stagesArray = Object.entries(funnel.stages).map(([id, stage]) => ({
    id,
    title: stage.name,
    color: stage.color,
    position: parseInt(stage.position, 10) || 0,
  }));

  stagesArray.sort((a, b) => a.position - b.position);
  return stagesArray;
});

// Primeiro estágio do funil (usado para "leads novos")
const firstStageId = computed(() => {
  if (!selectedFunnel.value?.stages) return null;
  const stages = Object.entries(selectedFunnel.value.stages).map(
    ([id, stage]) => ({
      id,
      position: parseInt(stage.position, 10) || 0,
    })
  );
  stages.sort((a, b) => a.position - b.position);
  return stages[0]?.id || null;
});

// Métricas do cockpit
const totalLeads = computed(() => {
  if (funnelStats.value) {
    return funnelStats.value.total_items || 0;
  }
  return funnelItems.value.length;
});

const newLeads = computed(() => {
  if (!firstStageId.value) return 0;
  return funnelItems.value.filter(
    item => String(item.funnel_stage) === String(firstStageId.value)
  ).length;
});

const activeAgentsCount = computed(() => {
  const ids = new Set();
  funnelItems.value.forEach(item => {
    // Dados atuais: lista de agentes atribuídos em item.assigned_agents
    if (Array.isArray(item.assigned_agents)) {
      item.assigned_agents.forEach(agent => {
        if (agent?.id) {
          ids.add(agent.id);
        }
      });
    }
  });
  return ids.size;
});

const activeAgentsList = computed(() => {
  const map = new Map();
  funnelItems.value.forEach(item => {
    if (Array.isArray(item.assigned_agents)) {
      item.assigned_agents.forEach(agent => {
        if (agent?.id && !map.has(agent.id)) {
          map.set(agent.id, {
            id: agent.id,
            name: agent.name,
            email: agent.email,
            avatar_url: agent.avatar_url,
          });
        }
      });
    }
  });
  return Array.from(map.values()).slice(0, 5);
});

const topAgent = computed(() => {
  const counts = new Map();

  funnelItems.value.forEach(item => {
    if (Array.isArray(item.assigned_agents)) {
      item.assigned_agents.forEach(agent => {
        if (!agent?.id) return;
        const current = counts.get(agent.id) || {
          id: agent.id,
          name: agent.name,
          email: agent.email,
          avatar_url: agent.avatar_url,
          count: 0,
        };
        current.count += 1;
        counts.set(agent.id, current);
      });
    }
  });

  if (!counts.size) return null;

  return Array.from(counts.values()).sort((a, b) => b.count - a.count)[0];
});

const totalValue = computed(() => {
  if (funnelStats.value) {
    return funnelStats.value.total_value || 0;
  }

  return funnelItems.value.reduce((sum, item) => {
    const raw = item.item_details?.value;
    const value = raw ? parseFloat(raw) : 0;
    if (Number.isNaN(value)) return sum;
    return sum + value;
  }, 0);
});

const leadsWithConversation = computed(() => {
  if (funnelStats.value) {
    return funnelStats.value.items_with_conversation || 0;
  }

  return funnelItems.value.filter(item => {
    return item.conversation || item.item_details?.conversation_id;
  }).length;
});

const leadsWithPendingChecklist = computed(() => {
  if (funnelStats.value) {
    return funnelStats.value.items_with_pending_checklist || 0;
  }

  return funnelItems.value.filter(item => {
    if (!Array.isArray(item.checklist)) return false;
    return item.checklist.some(check => !check.completed);
  }).length;
});

const unassignedLeads = computed(() => {
  if (funnelStats.value) {
    return funnelStats.value.items_without_agent || 0;
  }

  return funnelItems.value.filter(item => {
    return (
      !Array.isArray(item.assigned_agents) || item.assigned_agents.length === 0
    );
  }).length;
});

const winRate = computed(() => {
  if (!funnelStats.value || !funnelStats.value.total_items) return 0;
  const won = funnelStats.value.won_items || 0;
  const total = funnelStats.value.total_items || 0;
  return Math.round((won / total) * 100);
});

const conversationsWithoutItem = computed(() => {
  if (!funnelStats.value) return 0;
  return funnelStats.value.account_conversations_without_item || 0;
});

const newConversationsWithoutItem24h = computed(() => {
  if (!funnelStats.value) return 0;
  return funnelStats.value.account_new_conversations_without_item_24h || 0;
});

const contactsWaitingForReply = computed(() => {
  if (!funnelStats.value) return 0;
  return funnelStats.value.account_contacts_waiting_for_reply || 0;
});

// Métricas de SLA perdido por etapa vindas da API de funis (index)
const slaBreachedByStage = computed(() => {
  if (!funnelStats.value) return {};
  return funnelStats.value.sla_breached_by_stage || {};
});

const nextChecklistAction = computed(() => {
  const candidates = [];

  funnelItems.value.forEach(item => {
    if (!Array.isArray(item.checklist)) return;

    item.checklist.forEach(check => {
      if (check.completed) return;
      if (!check.due_date) return;

      const due = new Date(check.due_date);
      if (Number.isNaN(due.getTime())) return;

      candidates.push({
        item,
        check,
        due,
      });
    });
  });

  if (!candidates.length) return null;

  candidates.sort((a, b) => a.due - b.due);
  return candidates[0];
});

const nextChecklistMeta = computed(() => {
  if (!nextChecklistAction.value || !selectedFunnel.value) return null;

  const { item } = nextChecklistAction.value;
  const stage = selectedFunnel.value.stages?.[item.funnel_stage];
  const stageName = stage?.name || item.funnel_stage;

  let agents = [];
  if (Array.isArray(item.assigned_agents)) {
    agents = item.assigned_agents.map(agent => agent?.name).filter(Boolean);
  }

  const rawValue = item.item_details?.value;
  let formattedValue = null;
  if (rawValue) {
    const num = parseFloat(rawValue);
    if (!Number.isNaN(num)) {
      const currency = item.item_details.currency;
      const locale = currency?.locale || 'pt-BR';
      const code = currency?.code || 'BRL';
      formattedValue = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
      }).format(num);
    }
  }

  return {
    stageName,
    agents,
    formattedValue,
  };
});

// Etapas com itens em que o SLA configurado foi perdido (dados vindos do backend)
const slaBreachedStages = computed(() => {
  if (!selectedFunnel.value?.stages) return [];

  const stagesConfig = selectedFunnel.value.stages || {};
  const byStage = slaBreachedByStage.value || {};

  const result = Object.entries(byStage)
    .filter(([, count]) => Number(count) > 0)
    .map(([stageId, count]) => {
      const stage = stagesConfig[stageId] || {};
      return {
        id: stageId,
        name: stage.name || stageId,
        color: stage.color,
        count: Number(count),
      };
    });

  return result.sort((a, b) => b.count - a.count);
});

const topStages = computed(() => {
  if (!selectedFunnel.value?.stages) return [];

  const counts = {};
  funnelItems.value.forEach(item => {
    const stageId = item.funnel_stage;
    if (!stageId) return;
    counts[stageId] = (counts[stageId] || 0) + 1;
  });

  const result = Object.entries(counts).map(([id, count]) => {
    const stage = selectedFunnel.value.stages[id];
    return {
      id,
      name: stage?.name || `Etapa ${id}`,
      color: stage?.color,
      count,
    };
  });

  result.sort((a, b) => b.count - a.count);
  return result.slice(0, 3);
});

// Estado para integrar com o header
const searchResults = ref(null);
const activeFilters = ref(null);
const isDraggingActive = ref(false);
const activeDraggedItem = ref(null);

const handleFilter = filters => {
  activeFilters.value = filters || {};
};

const handleSearch = () => {
  // Mantém compatibilidade com o header; busca detalhada não é necessária no cockpit
};
</script>

<template>
  <div class="flex flex-col h-full bg-n-solid-1">
    <KanbanHeader
      :current-view="currentView"
      :current-stage="null"
      :search-results="searchResults"
      :columns="columns"
      :active-filters="activeFilters"
      :kanban-items="funnelItems"
      :is-dragging-active="isDraggingActive"
      :dragged-item="activeDraggedItem"
      @filter-applied="handleFilter"
      @global-filter-change="handleFilter"
      @search="handleSearch"
      @switch-view="(...args) => emit('switchView', ...args)"
    />

    <div
      class="flex-1 overflow-y-auto bg-n-solid-1 text-n-slate-12 kanban-dashboard-scroll"
    >
      <div class="w-full px-4 py-4 md:py-6 space-y-6">
        <!-- Banner global de SLA perdido -->
        <div
          v-if="slaBreachedStages.length"
          class="rounded-xl px-4 py-3 flex items-center justify-between bg-red-50 dark:bg-red-900/20 border border-red-600 dark:border-red-700"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span class="w-2 h-8 rounded-full bg-red-600 dark:bg-red-500" />
            <div class="min-w-0">
              <div
                class="text-xs font-semibold uppercase tracking-wide text-red-900 dark:text-red-200"
              >
                SLA de etapa perdido
              </div>
              <div
                class="text-sm font-medium truncate text-red-900 dark:text-red-200"
              >
                {{ slaBreachedStages.reduce((sum, s) => sum + s.count, 0) }}
                lead(s) com SLA estourado em
                {{ slaBreachedStages.length }} etapa(s).
              </div>
            </div>
          </div>
        </div>

        <!-- Banner de destaque do agente com mais itens -->
        <div
          v-if="topAgent"
          class="rounded-xl px-4 py-3 flex items-center justify-between bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-500 dark:border-indigo-700"
        >
          <div class="flex items-center gap-3 min-w-0">
            <div
              class="w-9 h-9 rounded-full border border-white bg-n-alpha-2 flex items-center justify-center text-sm font-medium text-n-slate-12 overflow-hidden"
            >
              <img
                v-if="topAgent.avatar_url"
                :src="topAgent.avatar_url"
                :alt="topAgent.name"
                class="w-full h-full object-cover"
              />
              <span v-else>
                {{
                  topAgent.name ? topAgent.name.charAt(0).toUpperCase() : '?'
                }}
              </span>
            </div>
            <div class="min-w-0">
              <div
                class="text-xs font-semibold uppercase tracking-wide text-n-slate-11 dark:text-indigo-300"
              >
                Agente com mais itens
              </div>
              <div class="text-sm font-medium text-n-slate-12 truncate">
                {{ topAgent.name || 'Agente sem nome' }}
              </div>
              <div class="text-[11px] text-n-slate-11 truncate">
                {{ topAgent.email }} · {{ topAgent.count }} item(ns) atribuídos
              </div>
            </div>
          </div>
        </div>

        <!-- Banner de próxima ação sugerida -->
        <div
          v-if="nextChecklistAction"
          class="rounded-xl px-4 py-3 flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700"
        >
          <div
            class="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-yellow-400 dark:bg-yellow-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1F2937"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="M12 8v4" />
              <path d="M12 16h.01" />
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-2">
              <div
                class="text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200"
              >
                Próxima ação sugerida
              </div>
              <div
                class="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200"
              >
                Checklist
              </div>
            </div>
            <div
              class="mt-1 text-sm font-medium truncate text-amber-900 dark:text-amber-100"
            >
              {{
                nextChecklistAction.item.item_details?.title ||
                'Item sem título'
              }}
            </div>
            <div
              class="mt-0.5 text-xs truncate text-amber-800 dark:text-amber-200"
            >
              {{ nextChecklistAction.check.text }}
            </div>
            <div
              class="mt-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300"
            >
              Prazo:
              {{
                new Date(nextChecklistAction.due).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              }}
            </div>
            <div
              v-if="nextChecklistMeta"
              class="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-amber-800 dark:text-amber-200"
            >
              <span
                class="px-2 py-0.5 rounded-full bg-n-solid-1/40 text-[10px] font-medium"
              >
                ID #{{ nextChecklistAction.item.id }}
              </span>
              <span
                v-if="nextChecklistMeta.stageName"
                class="px-2 py-0.5 rounded-full bg-n-solid-1/40 text-[10px] font-medium truncate"
              >
                Etapa: {{ nextChecklistMeta.stageName }}
              </span>
              <span
                v-if="
                  nextChecklistMeta.agents && nextChecklistMeta.agents.length
                "
                class="px-2 py-0.5 rounded-full bg-n-solid-1/40 text-[10px] font-medium truncate"
              >
                Agente(s): {{ nextChecklistMeta.agents.join(', ') }}
              </span>
              <span
                v-if="nextChecklistMeta.formattedValue"
                class="px-2 py-0.5 rounded-full bg-n-solid-1/40 text-[10px] font-medium"
              >
                Valor: {{ nextChecklistMeta.formattedValue }}
              </span>
            </div>
          </div>
        </div>

        <!-- Linha principal de cards -->
        <div class="mb-1">
          <h3 class="text-sm font-semibold text-n-slate-12">
            Visão geral do funil
          </h3>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <div class="rounded-2xl bg-n-solid-2 border border-n-weak px-4 py-3">
            <div
              class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              Leads no funil
            </div>
            <div class="mt-2 text-2xl font-extrabold">
              {{ totalLeads }}
            </div>
          </div>

          <div class="rounded-2xl bg-n-solid-2 border border-n-weak px-4 py-3">
            <div
              class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              Leads novos
            </div>
            <div class="mt-2 text-2xl font-extrabold text-emerald-500">
              {{ newLeads }}
            </div>
            <p class="mt-1 text-xs text-n-slate-11">
              Itens na primeira etapa do funil
            </p>
          </div>

          <div class="rounded-2xl bg-n-solid-2 border border-n-weak px-4 py-3">
            <div
              class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              Agentes ativos
            </div>
            <div class="mt-2 flex items-center justify-between gap-3">
              <div class="text-2xl font-extrabold text-blue-500">
                {{ activeAgentsCount }}
              </div>
              <div class="flex -space-x-2">
                <div
                  v-for="agent in activeAgentsList"
                  :key="agent.id"
                  class="w-7 h-7 rounded-full border border-white bg-n-alpha-2 text-[11px] font-medium text-n-slate-12 flex items-center justify-center overflow-hidden"
                >
                  <img
                    v-if="agent.avatar_url"
                    :src="agent.avatar_url"
                    :alt="agent.name"
                    class="w-full h-full object-cover"
                  />
                  <span v-else>
                    {{ agent.name ? agent.name.charAt(0).toUpperCase() : '?' }}
                  </span>
                </div>
              </div>
            </div>
            <p class="mt-1 text-xs text-n-slate-11">
              Com pelo menos um lead no funil
            </p>
          </div>

          <div class="rounded-2xl bg-n-solid-2 border border-n-weak px-4 py-3">
            <div
              class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              Taxa de ganho
            </div>
            <div class="mt-2 text-2xl font-extrabold text-emerald-600">
              {{ winRate }}%
            </div>
            <p class="mt-1 text-xs text-n-slate-11">
              Percentual de itens marcados como ganhos no funil
            </p>
          </div>
        </div>

        <!-- Banner de etapas com SLA perdido -->
        <div v-if="slaBreachedStages.length" class="space-y-2">
          <div
            v-for="stage in slaBreachedStages"
            :key="stage.id"
            class="flex items-center justify-between rounded-md px-3 py-2 bg-n-solid-2 border border-red-600 dark:border-red-700"
          >
            <div class="flex items-center gap-2 min-w-0">
              <span
                class="w-2 h-2 rounded-full"
                :style="{ backgroundColor: stage.color || '#F97316' }"
              />
              <div class="text-xs min-w-0">
                <div class="font-medium truncate text-n-slate-12">
                  Etapa {{ stage.name }} com SLA perdido
                </div>
                <div class="text-[11px] text-n-slate-12">
                  {{ stage.count }} lead(s) com SLA de etapa estourado
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Linha secundária de cards -->
        <div class="mt-6 mb-1">
          <h3 class="text-sm font-semibold text-n-slate-12">
            Detalhes dos leads
          </h3>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div class="rounded-2xl bg-n-solid-2 border border-n-weak px-4 py-3">
            <div
              class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              Valor total em aberto
            </div>
            <div class="mt-2 text-xl font-extrabold">
              {{
                new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(totalValue)
              }}
            </div>
          </div>

          <div class="rounded-2xl bg-n-solid-2 border border-n-weak px-4 py-3">
            <div
              class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              Leads com conversa
            </div>
            <div class="mt-2 text-2xl font-extrabold text-violet-500">
              {{ leadsWithConversation }}
            </div>
            <p class="mt-1 text-xs text-n-slate-11">
              Itens vinculados a conversas
            </p>
          </div>

          <div class="rounded-2xl bg-n-solid-2 border border-n-weak px-4 py-3">
            <div
              class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              Leads com tarefas pendentes
            </div>
            <div class="mt-2 text-2xl font-extrabold text-amber-500">
              {{ leadsWithPendingChecklist }}
            </div>
            <p class="mt-1 text-xs text-n-slate-11">
              Itens com checklist ainda não concluído
            </p>
          </div>
        </div>

        <!-- Resumo rápido de itens sem agente -->
        <div
          v-if="unassignedLeads"
          class="rounded-xl px-4 py-3 flex items-center justify-between bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700"
        >
          <div class="text-sm font-medium text-red-900 dark:text-red-200">
            {{ unassignedLeads }} lead(s) sem agente atribuído neste funil.
          </div>
          <div class="text-xs text-red-700 dark:text-red-300">
            Distribua esses leads entre o time para evitar gargalos.
          </div>
        </div>

        <!-- Conversas e contatos para ação rápida -->
        <div
          v-if="
            conversationsWithoutItem ||
            contactsWaitingForReply ||
            newConversationsWithoutItem24h
          "
          class="mt-6 mb-1"
        >
          <h3 class="text-sm font-semibold text-n-slate-12">
            Conversas e contatos para ação rápida
          </h3>
        </div>
        <div
          v-if="
            conversationsWithoutItem ||
            contactsWaitingForReply ||
            newConversationsWithoutItem24h
          "
          class="grid grid-cols-1 sm:grid-cols-3 gap-4"
        >
          <div
            v-if="contactsWaitingForReply"
            class="rounded-2xl px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700"
          >
            <div
              class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              Contatos aguardando resposta
            </div>
            <div
              class="mt-2 text-2xl font-extrabold text-red-500 dark:text-red-400"
            >
              {{ contactsWaitingForReply }}
            </div>
            <p class="mt-1 text-xs text-n-slate-11">
              Conversas sem primeira resposta ou aguardando nova interação.
            </p>
          </div>

          <div
            v-if="conversationsWithoutItem"
            class="rounded-2xl px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700"
          >
            <div
              class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              Conversas sem item no funil
            </div>
            <div class="mt-2 text-2xl font-extrabold text-n-slate-12">
              {{ conversationsWithoutItem }}
            </div>
            <p class="mt-1 text-xs text-n-slate-11">
              Crie itens para acompanhar essas oportunidades no Kanban.
            </p>
          </div>

          <div
            v-if="newConversationsWithoutItem24h"
            class="rounded-2xl px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700"
          >
            <div
              class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              Novas conversas sem item (24h)
            </div>
            <div
              class="mt-2 text-2xl font-extrabold text-amber-600 dark:text-amber-400"
            >
              {{ newConversationsWithoutItem24h }}
            </div>
            <p class="mt-1 text-xs text-n-slate-11">
              Priorize estruturar essas conversas em itens do funil.
            </p>
          </div>
        </div>

        <!-- Etapas com mais leads -->
        <div class="rounded-2xl bg-n-solid-2 border border-n-weak p-4">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              Etapas com mais leads
            </h3>
          </div>

          <div v-if="topStages.length" class="space-y-2">
            <div
              v-for="stage in topStages"
              :key="stage.id"
              class="flex items-center justify-between gap-3"
            >
              <div class="flex items-center gap-2 min-w-0">
                <span
                  class="w-2 h-2 rounded-full"
                  :style="{ backgroundColor: stage.color || '#64748B' }"
                />
                <span class="text-sm text-n-slate-12 truncate">
                  {{ stage.name }}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <div
                  class="w-24 h-1.5 rounded-full bg-n-alpha-2 overflow-hidden"
                >
                  <div
                    class="h-full rounded-full bg-woot-500"
                    :style="{
                      width:
                        Math.min(
                          100,
                          (stage.count / (topStages[0]?.count || 1)) * 100
                        ) + '%',
                    }"
                  />
                </div>
                <span
                  class="text-xs font-medium text-n-slate-11 w-8 text-right"
                >
                  {{ stage.count }}
                </span>
              </div>
            </div>
          </div>
          <div v-else class="text-xs text-n-slate-11 italic">
            Nenhum lead encontrado neste funil.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.kanban-dashboard-scroll {
  scrollbar-width: none; /* Firefox */
}

.kanban-dashboard-scroll::-webkit-scrollbar {
  width: 0;
  height: 0;
}
</style>
